/**
 * Types œuvres + tags + séries — alias FE typés sur le codegen OpenAPI.
 *
 * `frontend/src/types/api-generated.ts` est régénéré depuis les schémas Pydantic
 * du backend par `npm run gen:api` (cf `frontend/scripts/codegen-api-types.mjs`)
 * et n'est pas commité : `tsc` tourne toujours sur des types frais, en local
 * comme en CI — un schéma Pydantic qui bouge casse le typecheck FE, pas un diff.
 *
 * Ce module ré-exporte des alias **lisibles** depuis `components["schemas"]["..."]`
 * pour que les pages FE n'aient pas à fouiller la structure générée.
 */

import type { components } from './api-generated'

export type WorkResponse = components['schemas']['WorkResponse']
export type WorkSummaryResponse = components['schemas']['WorkSummaryResponse']
export type WorkListResponse = components['schemas']['WorkListResponse']
export type WorkUpdateRequest = components['schemas']['WorkUpdateRequest']
export type WorkMediaResponse = components['schemas']['WorkMediaResponse']

// Variantes — alias FE typés sur le codegen. Refonte POD v6.1 : la
// junction `work_variant_formats` (et son shape `WorkVariantFormatLink`)
// a disparu — une variante porte UN format singulier (`format_id`) +
// optionnellement UN `printable_spec_id` (binding POD).
export type WorkVariantWithCatalogResponse = components['schemas']['WorkVariantWithCatalogResponse']
export type WorkVariantInput = components['schemas']['WorkVariantInput']
export type WorkVariantUpdateRequest = components['schemas']['WorkVariantUpdateRequest']
export type BulkVariantCreateRequest = components['schemas']['BulkVariantCreateRequest']
export type EditionKind = components['schemas']['EditionKind']
export type WorkVariantStatus = components['schemas']['WorkVariantStatus']

// Deliverables digitaux (S2.5d) — fichiers générés JPG q100 + PDF.
export type WorkDeliverableResponse = components['schemas']['WorkDeliverableResponse']

// Preview live des livrables digitaux pour une œuvre (refonte 2026-05-14).
// Consommé par le modal variante en mode 'digital' pour afficher
// dynamiquement les formats résolus côté BE (intersection catalogue actif
// kind='digital' ∩ ratios masters de l'œuvre).
export type WorkDigitalFormatsPreviewResponse =
  components['schemas']['WorkDigitalFormatsPreviewResponse']

// `WorkCreateRequest` n'est PAS dans `api-generated.ts` parce que la route
// POST /api/works le consomme via FastAPI Body sans response_model dédié
// après le refactor autosave. On reflète la forme manuellement ; tout drift
// est attrapé par `audit_pydantic_db_alignment.py` côté BE.
//
// S2.5e : `width_cm`/`height_cm`/`medium_fr` retirés (migration 008) — les
// dimensions vendables vivent sur les variantes (`work_variant_formats`),
// le médium passe par `supports` catalogue + `technique_tags`.
export interface WorkCreateRequest {
  title_fr: string
  series_id?: number | null
  series_position?: number | null

  description_fr?: string | null
  story_fr?: string | null

  // Slugs validés par catalogue paramétrable Tier 2 (S2.5c) — le BE valide à
  // l'écriture qu'ils existent dans `emotional_intents` / `target_audiences`.
  emotional_intent?: string[] | null
  target_audience?: string[] | null
  technique_tags?: string[] | null
  influences?: string[] | null

  notes?: string | null

  created_at_artistic?: string | null

  tag_ids?: number[]
  variants?: WorkVariantInput[]
}

export type TagResponse = components['schemas']['TagResponse']
export type TagCreateRequest = components['schemas']['TagCreateRequest']

export type SeriesResponse = components['schemas']['SeriesResponse']
export type SeriesCreateRequest = components['schemas']['SeriesCreateRequest']
export type SeriesWorkSummary = components['schemas']['SeriesWorkSummary']
export type SeriesReorderRequest = components['schemas']['SeriesReorderRequest']

export type VisionJobResponse = components['schemas']['VisionJobResponse']

// ── Enums ────────────────────────────────────────────────────────────────────
// Note S2.5c : `EmotionalIntent` et `TargetAudience` ne sont plus des enums
// Pydantic — ce sont des slugs paramétrables (catalogue user-managed).
// L'UI fetch les options via `useEmotionalIntents()` / `useTargetAudiences()`
// (cf. `hooks/useCatalog.ts`). Plus de lookup FR codé en dur ici.
export type WorkStatus = components['schemas']['WorkStatus']
export type WorkOrientation = components['schemas']['WorkOrientation']
export type WorkSortKey = components['schemas']['WorkSortKey']
export type WorkMediaKind = components['schemas']['WorkMediaKind']
export type TagKind = components['schemas']['TagKind']
export type VisionJobStatus = components['schemas']['VisionJobStatus']

// ── Lookups FR pour l'UI atelier (slugs neutres EN -> labels FR) ─────────────
//
// Les enums BE sont des slugs neutres EN (consommés par Claude S3). L'UI
// atelier les présente en français via lookup local. Pas de doublon FR/EN
// en DB — le mapping vit ici.

export const WORK_STATUS_FR: Record<WorkStatus, string> = {
  draft: 'Brouillon',
  published: 'Publiée',
  archived: 'Archivée',
}

export const WORK_ORIENTATION_FR: Record<WorkOrientation, string> = {
  portrait: 'Portrait',
  landscape: 'Paysage',
  square: 'Carré',
}

export const WORK_SORT_FR: Record<WorkSortKey, string> = {
  relevance: 'Pertinence',
  recent: 'Récents',
  popular: 'Plus vus',
  engagement: 'Plus performants',
}

export const EDITION_KIND_FR: Record<EditionKind, string> = {
  open: 'Open edition',
  limited: 'Édition limitée',
  original: 'Original',
}

export const EDITION_KIND_OPTIONS: EditionKind[] = ['open', 'limited', 'original']

export const WORK_VARIANT_STATUS_FR: Record<WorkVariantStatus, string> = {
  draft: 'Brouillon',
  active: 'Actif',
  sold_out: 'Épuisé',
  archived: 'Archivé',
}

export const WORK_VARIANT_STATUS_OPTIONS: WorkVariantStatus[] = [
  'draft',
  'active',
  'sold_out',
  'archived',
]

export const TAG_KIND_FR: Record<TagKind, string> = {
  theme: 'Thème',
  color: 'Couleur',
  technique: 'Technique',
  mood: 'Ambiance',
  subject: 'Sujet',
  format: 'Format',
  influence: 'Influence',
  flag: 'Marqueur',
}

// ── Listes ordonnées pour les selects (préserve l'ordre éditorial) ──────────

export const WORK_STATUS_OPTIONS: WorkStatus[] = ['draft', 'published', 'archived']

export const WORK_ORIENTATION_OPTIONS: WorkOrientation[] = ['portrait', 'landscape', 'square']

export const WORK_SORT_OPTIONS: WorkSortKey[] = ['relevance', 'recent', 'popular', 'engagement']
